import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedSize from 'I18n/components/FormattedSize';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { FileAttachment } from 'foundations-components/transitional/components/FileAttachment';
import { FileButton } from 'foundations-components/transitional/components/FileButton';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { H1 } from 'foundations-components/transitional/components/H1';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { HR } from 'foundations-components/transitional/components/HR';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { UploadIcon } from 'foundations-assets/icons/components/UploadIcon';
import FileUploadComponent from 'FileManagerLib/components/upload/FileUploadComponent';
import { DropZoneNoBrowseFileAccess } from 'FileManagerLib/enums/FileAccess';
import { FileExtensionFilterEnum } from 'FileManagerLib/enums/FileExtensionFilters';

// @ts-expect-error Could not find a declaration file for module
import UIValidatedUrlFormControl from 'ui-addon-form-validation/Url/UIValidatedUrlFormControl';
import PracticalExerciseCondensedWrapper from './PracticalExerciseCondensedWrapper';
import { SubmissionAssetTypes } from '../practicalExerciseTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INITIAL_FILE_STATE = {
  fileUrl: null,
  importedFile: undefined
};
const ACCEPTED_TYPES = ['mp4', 'html', 'css', 'pdf', 'jpg', 'jpeg', 'png', 'mpg', 'mpeg', 'xls', 'xlsx', 'docx', 'doc'];
const ACCEPTED_TYPES_HUMAN = 'MP4, HTML, CSS, PDF, Word, Excel, JPEG, PNG';
const URL_VALIDATION_MESSAGES = {
  empty: '',
  validating: '',
  success: '',
  error: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.practicalExercise.invalidUrl"
  })
};
const PracticalExerciseWithSubmission = ({
  advanceToNextContent,
  isPassRequiredForCert,
  title,
  onSubmit,
  isSubmitting
}) => {
  const [fileState, setFileState] = useState(INITIAL_FILE_STATE);
  const [importedFileObject, setImportedFileObject] = useState();
  const [submissionLink, setSubmissionLink] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const handleOnFileChange = useCallback((fileUrl, importedFile) => {
    setFileState({
      fileUrl,
      importedFile
    });
  }, []);
  const handleOnFileAdd = useCallback(file => {
    setImportedFileObject(file);
  }, []);
  const handleCloseClick = useCallback(() => {
    setFileState(INITIAL_FILE_STATE);
    setImportedFileObject(undefined);
  }, []);
  const handleOnLinkInputChange = useCallback(e => {
    setSubmissionLink(e.target.value);
  }, []);
  const {
    importedFile
  } = fileState;
  const handleOnSubmitClick = useCallback(() => {
    const assetType = importedFile ? SubmissionAssetTypes.FILE_ID : SubmissionAssetTypes.SUBMISSION_URL;
    const reference = importedFile ? importedFile.get('id') : submissionLink;
    void onSubmit(assetType, reference);
  }, [importedFile, onSubmit, submissionLink]);
  return /*#__PURE__*/_jsx(PracticalExerciseCondensedWrapper, {
    buttons: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        "data-test-id": "skip-nonstarted-practical-exercise",
        marginInlineEnd: "300",
        onClick: advanceToNextContent,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.skip"
        })
      }), /*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "practical-exercise-review-button",
        disabled: submissionLink && !isValidUrl || !submissionLink && !importedFile,
        loading: isSubmitting,
        onClick: handleOnSubmitClick,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.submit"
        })
      })]
    }),
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      "data-test-id": "not-started-practical-exercise-manual-submission",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(Text, {
        textAlign: "center",
        children: /*#__PURE__*/_jsx(H1, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.manualPETitle",
            options: {
              title
            }
          })
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practicalExercise.uploadOrLink"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.practicalExercise.submittedGradingTime"
      }), !!isPassRequiredForCert || /*#__PURE__*/_jsx(Text, {
        marginBlockEnd: "500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.practicalExercise.reminder.required"
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "stretch",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          title: submissionLink ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.removeLink"
          }) : null,
          children: /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsxs("div", {
              style: {
                position: 'relative',
                pointerEvents: submissionLink ? 'none' : 'auto',
                opacity: submissionLink ? 0.3 : 1
              },
              children: [/*#__PURE__*/_jsx(FileUploadComponent, {
                closeable: true,
                fileData: importedFile && importedFile.get('id') ? importedFile : undefined,
                fileUploadOptions: {
                  overwrite: false,
                  folderPath: 'academy-practical-exercise-file-submissions'
                },
                filterType: FileExtensionFilterEnum.SUPPORTED,
                filteredExtensions: ACCEPTED_TYPES,
                icon: importedFile ? FileIcon : UploadIcon,
                onChange: handleOnFileChange,
                onClose: handleCloseClick,
                onFileAdd: handleOnFileAdd,
                showFileData: false,
                showFileName: false,
                uploadButtonRenderer: handleUploadClick => /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  flexDirection: "column",
                  children: [/*#__PURE__*/_jsxs("span", {
                    children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
                      message: "learningCenter.practicalExercise.dragAndDrop",
                      options: {
                        browse: /*#__PURE__*/_jsx(FileButton, {
                          onChange: e => {
                            handleUploadClick(e);
                            e.target.value = null;
                          },
                          use: "link",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "learningCenter.practicalExercise.dragAndDropBrowse"
                          })
                        })
                      }
                    }), /*#__PURE__*/_jsx(HelpIcon, {
                      marginInlineStart: "100",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.practicalExercise.acceptedTypes",
                        options: {
                          fileTypes: ACCEPTED_TYPES_HUMAN
                        }
                      }),
                      tooltipPlacement: "right"
                    })]
                  }), /*#__PURE__*/_jsx(FileButton, {
                    "data-test-id": "practical-exercise-upload-button",
                    marginBlockStart: "300",
                    onChange: e => {
                      handleUploadClick(e);
                      e.target.value = null;
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.practicalExercise.upload"
                    })
                  })]
                }),
                uploadedFileAccess: DropZoneNoBrowseFileAccess.HIDDEN_IN_APP_PRIVATE_NOT_INDEXABLE,
                children: importedFileObject && /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  flexDirection: "column",
                  children: [/*#__PURE__*/_jsxs("span", {
                    children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
                      message: "learningCenter.practicalExercise.dragAndDrop",
                      options: {
                        browse: /*#__PURE__*/_jsx(FileButton, {
                          disabled: true,
                          onChange: () => {},
                          use: "link",
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "learningCenter.practicalExercise.dragAndDropBrowse"
                          })
                        })
                      }
                    }), /*#__PURE__*/_jsx(HelpIcon, {
                      marginInlineStart: "100",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.practicalExercise.acceptedTypes",
                        options: {
                          fileTypes: ACCEPTED_TYPES_HUMAN
                        }
                      }),
                      tooltipPlacement: "right"
                    })]
                  }), /*#__PURE__*/_jsx(FileButton, {
                    disabled: true,
                    marginBlockStart: "300",
                    onChange: () => {},
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.practicalExercise.upload"
                    })
                  })]
                })
              }), importedFile && importedFileObject && /*#__PURE__*/_jsx(Flex, {
                alignItems: "center",
                insetBlockEnd: "0",
                insetBlockStart: "0",
                insetInlineEnd: "0",
                insetInlineStart: "0",
                justifyContent: "center",
                position: "absolute",
                children: /*#__PURE__*/_jsx(FileAttachment, {
                  closeable: true,
                  fileInfo: /*#__PURE__*/_jsx(FormattedSize, {
                    value: importedFileObject.size
                  }),
                  fileName: importedFileObject.name,
                  fileType: importedFileObject.type,
                  onCloseClick: handleCloseClick,
                  padding: "1100",
                  processing: importedFileObject && !importedFile,
                  children: /*#__PURE__*/_jsx(NanoProgress, {
                    boxShadow: false
                  })
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          paddingInline: "1000",
          children: [/*#__PURE__*/_jsx(HR, {
            distance: "small",
            marginInlineEnd: "300"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.or"
            // @ts-expect-error Untyped
            ,
            style: {
              opacity: importedFileObject || submissionLink ? 0.5 : 1
            }
          }), /*#__PURE__*/_jsx(HR, {
            distance: "small",
            marginInlineStart: "300"
          })]
        }), /*#__PURE__*/_jsx(UIValidatedUrlFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.addSubmissionLink"
          }),
          labelTooltip: importedFileObject ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.practicalExercise.removeFile"
          }) : null,
          onError: () => setIsValidUrl(false),
          onSuccess: () => setIsValidUrl(true),
          validationMessage: URL_VALIDATION_MESSAGES,
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "practical-exercise-url-field",
            disabled: !!importedFileObject,
            name: "submission-link",
            onChange: handleOnLinkInputChange,
            value: submissionLink
          })
        })]
      })]
    })
  });
};
export default PracticalExerciseWithSubmission;